import React, { useState, useEffect } from "react";
import BreadCrumb from "../components/BreadCrumb";
import Meta from "../components/Meta";
import Container from "../components/Container";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile, getUserAddresses, saveUserAddress, deleteUserAddress, setDefaultAddress } from "../features/user/userSlice";
import { FiEdit, FiPlus, FiTrash2, FiCheck } from "react-icons/fi";

let profileSchema = yup.object({
  firstname: yup.string().required("First Name is Required"),
  lastname: yup.string().required("Last Name is Required"),
  email: yup
    .string()
    .required("Email is Required")
    .email("Email Should be valid"),
  mobile: yup.number().required().positive().integer("Mobile No is Required"),
});

const addressSchema = yup.object({
  firstname: yup.string().required("First Name is Required"),
  lastname: yup.string().required("Last Name is Required"),
  address: yup.string().required("Address is Required"),
  city: yup.string().required("City is Required"),
  state: yup.string().required("State is Required"),
  country: yup.string().required("Country is Required"),
  pincode: yup.string().required("Pincode is Required"),
});

const Profile = () => {
  const getTokenFromLocalStorage = localStorage.getItem("customer")
    ? JSON.parse(localStorage.getItem("customer"))
    : null;

  const config2 = {
    headers: {
      Authorization: `Bearer ${
        getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
      }`,
      Accept: "application/json",
    },
  };
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.auth.user);
  const userAddresses = useSelector((state) => state.auth.userAddresses);
  const [edit, setEdit] = useState(true);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  
  const formik = useFormik({
    initialValues: {
      firstname: userState?.firstname,
      lastname: userState?.lastname,
      email: userState?.email,
      mobile: userState?.mobile,
    },
    validationSchema: profileSchema,
    onSubmit: (values) => {
      dispatch(updateProfile({ data: values, config2: config2 }));
      setEdit(true);
    },
  });

  const addressFormik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      address: "",
      city: "",
      state: "",
      country: "India",
      pincode: "",
      other: "",
      isDefault: false,
    },
    validationSchema: addressSchema,
    onSubmit: (values) => {
      if (editingAddressId) {
        // Update existing address
        dispatch(saveUserAddress(values, config2));
      } else {
        // Add new address
        dispatch(saveUserAddress(values, config2));
      }
      setShowAddressForm(false);
      setEditingAddressId(null);
      addressFormik.resetForm();
    },
  });

  useEffect(() => {
    dispatch(getUserAddresses(config2));
  }, [dispatch]);

  const handleEditAddress = (address) => {
    setEditingAddressId(address._id);
    addressFormik.setValues({
      firstname: address.firstname || "",
      lastname: address.lastname || "",
      address: address.address || "",
      city: address.city || "",
      state: address.state || "",
      country: address.country || "India",
      pincode: address.pincode || "",
      other: address.other || "",
      isDefault: address.isDefault || false,
    });
    setShowAddressForm(true);
  };

  const handleDeleteAddress = (addressId) => {
    dispatch(deleteUserAddress(addressId, config2));
  };

  const handleSetDefaultAddress = (addressId) => {
    dispatch(setDefaultAddress(addressId, config2));
  };

  // Schema markup for structured data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "My Profile - VEDA Merchandise",
    "description": "Manage your profile information and saved addresses at VEDA Merchandise.",
    "url": "https://vedamerchandise.com/my-profile",
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  return (
    <>
      <Meta 
        title="My Profile - VEDA Merchandise"
        description="Manage your profile information and saved addresses at VEDA Merchandise."
        keywords="my profile, profile, VEDA profile, account settings"
        author="VEDA Merchandise"
        ogTitle="My Profile - VEDA Merchandise"
        ogDescription="Manage your profile information and saved addresses at VEDA Merchandise."
        ogImage="https://vedamerchandise.com/profile-preview.jpg"
        ogUrl="https://vedamerchandise.com/my-profile"
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="My Profile - VEDA Merchandise"
        twitterDescription="Manage your profile information and saved addresses at VEDA Merchandise."
        twitterImage="https://vedamerchandise.com/profile-preview.jpg"
        robots="noindex, nofollow"
        canonicalUrl="https://vedamerchandise.com/my-profile"
        schemaMarkup={schemaMarkup}
      />
      <Container class1="cart-wrapper home-wrapper-2 py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8">
            <div className="d-flex justify-content-between align-items-center mb-6">
              <h3 className="my-3 text-3xl bg-yellow-200 text-gray-800 py-2 px-6 rounded-full font-bold">Update Profile</h3>
              <button 
                className="flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full p-3 transition-colors duration-300 shadow-lg"
                onClick={() => setEdit(false)}
              >
                <FiEdit className="text-lg" />
              </button>
            </div>
            
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <form onSubmit={formik.handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-6">
                    <label htmlFor="example1" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstname"
                      className={`w-full rounded-xl px-4 py-3 border ${
                        formik.touched.firstname && formik.errors.firstname
                          ? "border-red-500"
                          : "border-gray-300"
                      } focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition`}
                      id="example1"
                      disabled={edit}
                      placeholder="Enter your first name"
                      value={formik.values.firstname || ""}
                      onChange={formik.handleChange("firstname")}
                      onBlur={formik.handleBlur("firstname")}
                    />
                    <div className="text-red-500 text-sm mt-1 min-h-5">
                      {formik.touched.firstname && formik.errors.firstname}
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-6">
                    <label htmlFor="example2" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastname"
                      className={`w-full rounded-xl px-4 py-3 border ${
                        formik.touched.lastname && formik.errors.lastname
                          ? "border-red-500"
                          : "border-gray-300"
                      } focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition`}
                      id="example2"
                      disabled={edit}
                      placeholder="Enter your last name"
                      value={formik.values.lastname || ""}
                      onChange={formik.handleChange("lastname")}
                      onBlur={formik.handleBlur("lastname")}
                    />
                    <div className="text-red-500 text-sm mt-1 min-h-5">
                      {formik.touched.lastname && formik.errors.lastname}
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-6">
                    <label htmlFor="exampleInputEmail1" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      className={`w-full rounded-xl px-4 py-3 border ${
                        formik.touched.email && formik.errors.email
                          ? "border-red-500"
                          : "border-gray-300"
                      } focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition`}
                      id="exampleInputEmail1"
                      disabled={edit}
                      placeholder="Enter your email"
                      value={formik.values.email || ""}
                      onChange={formik.handleChange("email")}
                      onBlur={formik.handleBlur("email")}
                    />
                    <div className="text-red-500 text-sm mt-1 min-h-5">
                      {formik.touched.email && formik.errors.email}
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-6">
                    <label htmlFor="example3" className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      className={`w-full rounded-xl px-4 py-3 border ${
                        formik.touched.mobile && formik.errors.mobile
                          ? "border-red-500"
                          : "border-gray-300"
                      } focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition`}
                      id="example3"
                      disabled={edit}
                      placeholder="Enter your mobile number"
                      value={formik.values.mobile || ""}
                      onChange={formik.handleChange("mobile")}
                      onBlur={formik.handleBlur("mobile")}
                    />
                    <div className="text-red-500 text-sm mt-1 min-h-5">
                      {formik.touched.mobile && formik.errors.mobile}
                    </div>
                  </div>
                </div>

                {edit === false && (
                  <div className="mt-8">
                    <button 
                      type="submit" 
                      className="inline-flex items-center bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-full font-semibold transition-colors duration-300 shadow-lg hover:shadow-xl"
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </form>
            </div>
            
            {/* Address Management Section */}
            <div className="mt-8 bg-white rounded-3xl shadow-xl p-8">
              <div className="d-flex justify-content-between align-items-center mb-6">
                <h4 className="text-xl font-bold text-gray-800">Saved Addresses</h4>
                <button 
                  className="flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full p-3 transition-colors duration-300 shadow-lg"
                  onClick={() => {
                    setShowAddressForm(true);
                    setEditingAddressId(null);
                    addressFormik.resetForm();
                  }}
                >
                  <FiPlus className="text-lg" />
                </button>
              </div>
              
              {/* Address Form */}
              {showAddressForm && (
                <div className="mb-8 p-6 bg-gray-50 rounded-2xl">
                  <h5 className="text-lg font-bold text-gray-800 mb-4">
                    {editingAddressId ? "Edit Address" : "Add New Address"}
                  </h5>
                  <form onSubmit={addressFormik.handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name
                        </label>
                        <input
                          type="text"
                          name="firstname"
                          className={`w-full rounded-xl px-4 py-2 border ${
                            addressFormik.touched.firstname && addressFormik.errors.firstname
                              ? "border-red-500"
                              : "border-gray-300"
                          } focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition`}
                          placeholder="First Name"
                          value={addressFormik.values.firstname}
                          onChange={addressFormik.handleChange("firstname")}
                          onBlur={addressFormik.handleBlur("firstname")}
                        />
                        {addressFormik.touched.firstname && addressFormik.errors.firstname && (
                          <div className="text-red-500 text-sm mt-1">
                            {addressFormik.errors.firstname}
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name
                        </label>
                        <input
                          type="text"
                          name="lastname"
                          className={`w-full rounded-xl px-4 py-2 border ${
                            addressFormik.touched.lastname && addressFormik.errors.lastname
                              ? "border-red-500"
                              : "border-gray-300"
                          } focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition`}
                          placeholder="Last Name"
                          value={addressFormik.values.lastname}
                          onChange={addressFormik.handleChange("lastname")}
                          onBlur={addressFormik.handleBlur("lastname")}
                        />
                        {addressFormik.touched.lastname && addressFormik.errors.lastname && (
                          <div className="text-red-500 text-sm mt-1">
                            {addressFormik.errors.lastname}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <input
                        type="text"
                        name="address"
                        className={`w-full rounded-xl px-4 py-2 border ${
                          addressFormik.touched.address && addressFormik.errors.address
                            ? "border-red-500"
                            : "border-gray-300"
                        } focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition`}
                        placeholder="Street address"
                        value={addressFormik.values.address}
                        onChange={addressFormik.handleChange("address")}
                        onBlur={addressFormik.handleBlur("address")}
                      />
                      {addressFormik.touched.address && addressFormik.errors.address && (
                        <div className="text-red-500 text-sm mt-1">
                          {addressFormik.errors.address}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Apartment, Suite, etc. (Optional)
                      </label>
                      <input
                        type="text"
                        name="other"
                        className="w-full rounded-xl px-4 py-2 border border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition"
                        placeholder="Apartment, suite, etc."
                        value={addressFormik.values.other}
                        onChange={addressFormik.handleChange("other")}
                        onBlur={addressFormik.handleBlur("other")}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City
                        </label>
                        <input
                          type="text"
                          name="city"
                          className={`w-full rounded-xl px-4 py-2 border ${
                            addressFormik.touched.city && addressFormik.errors.city
                              ? "border-red-500"
                              : "border-gray-300"
                          } focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition`}
                          placeholder="City"
                          value={addressFormik.values.city}
                          onChange={addressFormik.handleChange("city")}
                          onBlur={addressFormik.handleBlur("city")}
                        />
                        {addressFormik.touched.city && addressFormik.errors.city && (
                          <div className="text-red-500 text-sm mt-1">
                            {addressFormik.errors.city}
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State
                        </label>
                        <select
                          name="state"
                          className={`w-full rounded-xl px-4 py-2 border ${
                            addressFormik.touched.state && addressFormik.errors.state
                              ? "border-red-500"
                              : "border-gray-300"
                          } focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition`}
                          value={addressFormik.values.state}
                          onChange={addressFormik.handleChange("state")}
                          onBlur={addressFormik.handleBlur("state")}
                        >
                          <option value="">Select State</option>
                          <option value="Maharashtra">Maharashtra</option>
                        </select>
                        {addressFormik.touched.state && addressFormik.errors.state && (
                          <div className="text-red-500 text-sm mt-1">
                            {addressFormik.errors.state}
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pincode
                        </label>
                        <input
                          type="text"
                          name="pincode"
                          className={`w-full rounded-xl px-4 py-2 border ${
                            addressFormik.touched.pincode && addressFormik.errors.pincode
                              ? "border-red-500"
                              : "border-gray-300"
                          } focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition`}
                          placeholder="Pincode"
                          value={addressFormik.values.pincode}
                          onChange={addressFormik.handleChange("pincode")}
                          onBlur={addressFormik.handleBlur("pincode")}
                        />
                        {addressFormik.touched.pincode && addressFormik.errors.pincode && (
                          <div className="text-red-500 text-sm mt-1">
                            {addressFormik.errors.pincode}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <select
                        name="country"
                        className={`w-full rounded-xl px-4 py-2 border ${
                          addressFormik.touched.country && addressFormik.errors.country
                            ? "border-red-500"
                            : "border-gray-300"
                        } focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none transition`}
                        value={addressFormik.values.country}
                        onChange={addressFormik.handleChange("country")}
                        onBlur={addressFormik.handleBlur("country")}
                      >
                        <option value="India">India</option>
                      </select>
                      {addressFormik.touched.country && addressFormik.errors.country && (
                        <div className="text-red-500 text-sm mt-1">
                          {addressFormik.errors.country}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="isDefault"
                        id="isDefault"
                        className="mr-2"
                        checked={addressFormik.values.isDefault}
                        onChange={addressFormik.handleChange("isDefault")}
                      />
                      <label htmlFor="isDefault" className="text-sm text-gray-700">
                        Set as default address
                      </label>
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        className="inline-flex items-center bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-full font-semibold transition-colors duration-300 shadow-lg hover:shadow-xl"
                      >
                        {editingAddressId ? "Update Address" : "Save Address"}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-full font-semibold transition-colors duration-300 shadow-lg hover:shadow-xl"
                        onClick={() => {
                          setShowAddressForm(false);
                          setEditingAddressId(null);
                          addressFormik.resetForm();
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
              
              {/* Saved Addresses List */}
              <div className="space-y-4">
                {userAddresses && userAddresses.length > 0 ? (
                  userAddresses.map((address) => (
                    <div key={address._id} className="border border-gray-200 rounded-2xl p-4 relative">
                      {address.isDefault && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full flex items-center">
                          <FiCheck className="mr-1" /> Default
                        </div>
                      )}
                      <div className="flex justify-between items-start">
                        <div>
                          <h6 className="font-bold text-gray-900">
                            {address.firstname} {address.lastname}
                          </h6>
                          <p className="text-gray-600 text-sm">
                            {address.address}
                            {address.other && `, ${address.other}`}
                            <br />
                            {address.city}, {address.state} {address.pincode}
                            <br />
                            {address.country}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            className="p-2 text-gray-500 hover:text-red-500"
                            onClick={() => handleEditAddress(address)}
                          >
                            <FiEdit />
                          </button>
                          <button
                            className="p-2 text-gray-500 hover:text-red-500"
                            onClick={() => handleDeleteAddress(address._id)}
                          >
                            <FiTrash2 />
                          </button>
                          {!address.isDefault && (
                            <button
                              className="p-2 text-gray-500 hover:text-red-500"
                              onClick={() => handleSetDefaultAddress(address._id)}
                            >
                              <FiCheck />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    No saved addresses. Add your first address!
                  </p>
                )}
              </div>
            </div>
            
            <div className="mt-8 bg-white rounded-3xl shadow-xl p-8">
              <h4 className="text-xl font-bold text-gray-800 mb-6">Account Information</h4>
              <div className="row">
                <div className="col-md-6 mb-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-sm text-gray-500 mb-1">Member Since</p>
                    <p className="font-medium">
                      {userState?.createdAt 
                        ? new Date(userState.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="col-md-6 mb-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-sm text-gray-500 mb-1">Last Updated</p>
                    <p className="font-medium">
                      {userState?.updatedAt 
                        ? new Date(userState.updatedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default Profile;